import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

const UserProfileService = {
    /**
     * Cancel a subscription
     * @returns {Promise<Object>} The API response
     */
    cancelTutorSubscription: async () => {
        const response = await ApiService.putData("/home/stripe/tutor/cancel");

        if (response && response.code === "FAILURE") {
            throw new Error(response.message || "Failed to cancel subscription");
        }

        return response;
    },

    cancelSubscription: async () => {
        const response = await ApiService.putData("/home/stripe/cancel");
        
        if (response && response.code === 'FAILURE') {
            throw new Error(response.message || 'Failed to cancel subscription');
        }
        
        return response;
    },

    /**
     * Reactivate a canceled subscription
     * @returns {Promise<Object>} The API response
     */
    reactivateSubscription: async () => {
        const response = await ApiService.putData("/home/stripe/reactivate");
        
        if (response && response.code === 'FAILURE') {
            throw new Error(response.message || 'Failed to reactivate subscription');
        }
        
        return response;
    },

    /**
     * Get payment history
     * @param {number} page - Page number (0-based)
     * @param {number} pageSize - Number of items per page
     * @param {string} sort - Sort string (e.g., "createdAt:desc")
     * @returns {Promise<Object>} Paginated payment history response
     */
    getPaymentHistory: async (page = 0, pageSize = 10, sort = "createdAt:desc") => {
        try {
            const params = {
                page: page,
                pageSize: pageSize,
                sort: sort
            };

            const response = await ApiService.getData("/home/payment/history", params);
            Logger.log("Fetched payment history:", response);

            // Handle different possible response structures
            if (response && typeof response === 'object') {
                // Check if response has an error code
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to fetch payment history");
                }
                
                // If response is paginated (has content, totalElements, etc.)
                if (response.content !== undefined) {
                    const p = response.page;
                    return {
                        content: response.content || [],
                        totalElements: p?.totalElements ?? 0,
                        totalPages: p?.totalPages ?? 0,
                        number: p?.number ?? page,
                        size: p?.size ?? response.size ?? pageSize
                    };
                }
                
                // Payments might be an array directly, or wrapped in 'data'
                if (Array.isArray(response)) {
                    return {
                        content: response,
                        totalElements: response.length,
                        totalPages: 1,
                        number: page,
                        size: pageSize
                    };
                }
                
                // If wrapped in 'data'
                if (response.data) {
                    const p = response.page;
                    return {
                        content: Array.isArray(response.data) ? response.data : [],
                        totalElements: p?.totalElements ?? (Array.isArray(response.data) ? response.data.length : 0),
                        totalPages: p?.totalPages ?? 1,
                        number: p?.number ?? page,
                        size: p?.size ?? response.size ?? pageSize
                    };
                }
            }
            
            // Return empty paginated response if response is null/undefined
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                number: page,
                size: pageSize
            };
        } catch (error) {
            Logger.error("Error fetching payment history:", error);
            throw error;
        }
    },

    /**
     * Get Stripe customer payment information
     * @returns {Promise<Object>} Payment information including billing email and payment methods
     */
    getPaymentInfo: async () => {
        try {
            const response = await ApiService.getData("/home/stripe/customer/payment-info");
            
            if (response && response.code === "FAILURE") {
                throw new Error(response.message || "Failed to fetch payment information");
            }
            
            return response;
        } catch (error) {
            Logger.error("Error fetching payment info:", error);
            throw error;
        }
    },

    /**
     * Create Stripe customer portal session
     * @returns {Promise<Object>} Portal session response with portalUrl
     */
    createPortalSession: async () => {
        try {
            const response = await ApiService.postData("/home/stripe/customer/portal-session", {});
            
            if (response && response.code === "FAILURE") {
                throw new Error(response.message || "Failed to create portal session");
            }
            
            return response;
        } catch (error) {
            Logger.error("Error creating portal session:", error);
            throw error;
        }
    },

    /**
     * Update subscription with new price
     * @param {string} priceId - The Stripe price ID to change to
     * @param {number} months - The number of months for the subscription period
     * @returns {Promise<Object>} Updated subscription response
     */
    /**
     * Request deletion of all user data. Returns confirmation code and status URL.
     * @returns {Promise<{ confirmationCode: string, statusUrl: string }>}
     */
    requestDataDeletion: async () => {
        try {
            const response = await ApiService.postData("/home/user/data-deletion", {});
            if (response && response.code === "FAILURE") {
                throw new Error(response.message || "Failed to request data deletion");
            }
            return response;
        } catch (error) {
            Logger.error("Error requesting data deletion:", error);
            throw error;
        }
    },

    changePlan: async (priceId, months) => {
        try {
            const requestBody = {
                priceId: priceId,
                months: months
            };

            const response = await ApiService.putData("/home/stripe/change-plan", requestBody);
            
            if (response && response.code === "FAILURE") {
                throw new Error(response.message || "Failed to update subscription");
            }
            
            return response;
        } catch (error) {
            Logger.error("Error updating subscription:", error);
            throw error;
        }
    }
};

export default UserProfileService;

